import { EventManager, LoggerUtilsComponent, ShellBrowserPlatform } from '@proteo/core';
import { navigateToUrl, registerApplication, setUnmountMaxTime, start } from 'single-spa';
import { constructApplications, constructLayoutEngine, constructRoutes } from 'single-spa-layout';
import 'zone.js';
import { PortalConfig } from './models/portal-config';
import { Process } from './models/process.model';
import { CheckNavigator } from './services/check-navigator.service';
import { ParamsService } from './services/param.service';
import {
  getCommonLanguage,
  getSharedConfig,
  navigateTo,
  navigateToError,
  setClbEnabled,
  setCommonAuth,
  setCommonLanguage,
  setCommonProcessId,
  setCto,
  setLandingData,
  setOrg,
  setPayEnabled,
  setPortalConfig
} from './services/portal-services';
import { ProcessService } from './services/process.service';
import { TokenService } from './services/token.service';

const processService = new ProcessService();
const checkNavigatorService = new CheckNavigator();
const tokenService = new TokenService();
const paramsService = new ParamsService(tokenService);
// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/ban-types
let portalConfig: PortalConfig;

new ShellBrowserPlatform<PortalConfig>().bootstrap({ CONFIG_BASE_PATH: '/conf/portal/onboarding/v0/' }).then((config) => {
  const routes = constructRoutes(document.querySelector('#onboarding-layout') as HTMLTemplateElement);
  LoggerUtilsComponent.getInstance().debug('[main.ts] config: ', config);
  // LoggerUtilsComponent.getInstance().debug('[main.ts] routes: ', routes);

  const applications = constructApplications({
    routes,
    loadApp({ name }) {
      return System.import(name);
    }
  });

  /**
   * The constructLayoutEngine API transforms your resolvedRoutes and applications into a layoutEngine object.
   * The layout engine is responsible for creating, destroying, and rearranging dom elements during route transitions.
   */
  portalConfig = config;
  setPortalConfig(config);

  //Load dynatrace script
  const node = document.createElement('script');
  node.type = 'text/javascript';
  node.src = config.CUSTOM_CONFIG.DYNATRACE;
  node.crossOrigin = 'anonymous';
  document.getElementsByTagName('head')[0].prepend(node);

  //Load captcha script

  const captcha = document.createElement('script');
  captcha.type = 'text/javascript';
  const urlParams = new URLSearchParams(window.location.search);
  const siteKeyFromConf = config.CUSTOM_CONFIG.SITEKEY;
  setLandingData(null);
  if (urlParams.get('site-key') !== null) {
    setLandingData(urlParams.get('site-key'));
    captcha.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + urlParams.get('site-key');
  } else {
    captcha.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + siteKeyFromConf;
  }
  document.getElementsByTagName('head')[0].appendChild(captcha);

  //Add collective variable in session
  const cto = urlParams.get('cto');
  if (cto !== null && cto !== '') {
    setCto(cto);
  }

  //set Collaborator value
  const clb = urlParams.get('clb');
  if (clb === 'true') {
    setClbEnabled();
  }

  //set Pay (yes/no) value operational log
  const pay = urlParams.get('pay');
  if (pay === 'true') {
    setPayEnabled();
  }

  //set the org url parameter
  const org = urlParams.get('org');
  if (org !== null && org !== '') {
    setOrg(org);
  }

  const layoutEngine = constructLayoutEngine({ routes, applications });
  applications.forEach((conf) => {
    const mfeCustomConfig = {
      customProps: {
        language: config.INIT_DATA.language,
        mfeConf: config.MICROFRONTEND_METADATA.find((e) => e.ID === conf.name),
        initData: config.INIT_DATA.externalData,
        sharedData: getSharedConfig
      }
    };
    registerApplication({ ...conf, ...mfeCustomConfig });
  });
  layoutEngine.activate();
  start();

  /**
   * Subscribing to MFE events (update user identifier from proteo landing)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  EventManager.getInstance().subscribe('SET_COMMON_AUTH', (message: string, data: any) => {
    LoggerUtilsComponent.getInstance().debug(`[main.ts] ${message} received. Data:`, data);
    setCommonAuth(data.data);
  });

  EventManager.getInstance().subscribe('COOKIES_POLICY_ACCEPTED', () => {
    LoggerUtilsComponent.getInstance().debug('Event cookies accepted');
    setTimeout(() => {
      EventManager.getInstance().publish('PORTAL_COOKIES_ACCEPTED');
    }, 1000);
  });

  EventManager.getInstance().subscribe('SET_PROCESS_ID', (message: string, data: any) => {
    LoggerUtilsComponent.getInstance().debug(`[main.ts] ${message} received. Data:`, data);
    setCommonProcessId(data.data);
  });

  EventManager.getInstance().subscribe('FINISH', (message: string, data: Record<string, string>) => {
    const mfeName = message.split('.')[1];
    if (mfeName === 'IDENTITY_CHECK') {
      LoggerUtilsComponent.getInstance().debug('Setting unmount max time die on timeout to false on case identity_check');
      setUnmountMaxTime(3000, false);
    }
    LoggerUtilsComponent.getInstance().debug(`[main.ts] ${message} received. Data:`, data);

    let messageLoaderTitle1: string;
    let messageLoaderText1: string;
    let messageLoaderTitle2: string;
    let messageLoaderText2: string;

    switch (getCommonLanguage()) {
      case 'es-ES':
        messageLoaderTitle1 = 'Solo tardaremos unos segundos';
        messageLoaderText1 = 'Estamos revisando tu vídeo y tu documentación. Por favor, no abandones ni actualices la pantalla.';
        messageLoaderTitle2 = '¡Ya queda menos!';
        messageLoaderText2 = 'Por favor, no abandones ni actualices la pantalla.';
        break;
      case 'ca-ES':
        messageLoaderTitle1 = 'Només trigarem uns segons';
        messageLoaderText1 = 'Estem revisant el teu vídeo i la teva documentació. Si us plau, no abandonis ni actualitzis la pantalla.';
        messageLoaderTitle2 = 'Ja queda menys!';
        messageLoaderText2 = 'Si us plau, no abandonis ni actualitzis la pantalla.';
        break;
      case 'en-GB':
        messageLoaderTitle1 = 'This will only take a few seconds';
        messageLoaderText1 = 'We are reviewing your video and documents. Please don’t leave or refresh this page.';
        messageLoaderTitle2 = 'Almost done!';
        messageLoaderText2 = 'Please don’t leave or update this page.';
        break;
    }
    // Show loader
    if (mfeName !== 'LOGIN') {
      const loader = document.createElement('bs-loading');
      loader.setAttribute('id', 'loader');
      loader.setAttribute('style', '--_bs-loading-background: #FFFFFF;');

      if (mfeName === 'IDENTITY_CHECK' && !(data as any).errorCode) {
        const jsonData: Record<string, string | number> = JSON.parse(data.jsonData);
        if (jsonData.numCheck === 1) {
          loader.setAttribute('loading-title', messageLoaderTitle1);
          loader.setAttribute('loading-text', messageLoaderText1);
        }
      }
      const body = document.getElementsByTagName('body')[0];
      body.appendChild(loader);
    }
    navigateTo(message, data);
  });

  EventManager.getInstance().subscribe('ERROR', (message: string, data: { data: { appID } }) => {
    LoggerUtilsComponent.getInstance().debug(`[main.ts] ${message} received. Data:`, data);
    if (data.data.appID !== 'RESTORE_PASSWORD') {
      navigateToError(data);
    }
  });

  EventManager.getInstance().subscribe('ERROR.LOGIN', (message: unknown, data: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    LoggerUtilsComponent.getInstance().debug(`[main.ts] ${message} received. Data:`, (data as any).message);
    // TODO: handle errors on login mfe
  });

  EventManager.getInstance().subscribe('TRANSLATE', (message: unknown, data: { isoLanguage: string }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    LoggerUtilsComponent.getInstance().debug(`[main.ts] ${message} received. Data:`, data);
    setCommonLanguage(data.isoLanguage);
  });

  EventManager.getInstance().subscribe(
    'PROCESS_LOG',
    async (message: unknown, data: { name: string; stage: string; level: string; message: string; description: string }) => {
      LoggerUtilsComponent.getInstance().debug(`[main.ts] ${message} PROCESS_LOG Data:`, data);
      await processService.postProcessLog(data.name, data.stage, data.level, data.message, data.description);
    }
  );

  EventManager.getInstance().subscribe('GET_PROCESS_SECOND_HOLDER', async (message: string, data: { data: { appID } }) => {
    LoggerUtilsComponent.getInstance().debug(`[main.ts] GET_PROCESS_SECOND_HOLDER Data:`, data);
    const processData: Process = await processService.getProcess('EXPANSIONDIGITAL');
    setCommonProcessId(processData.processId);

    if (data.data.appID === 'PERSONAL_DATA') {
      navigateToError({ error: '11', relatedOwner: processData.relatedOwner });
    } else {
      navigateTo(message, { relatedOwner: processData.relatedOwner });
    }
  });
});

/**
 * A single-spa:before-routing-event event is fired before every routing event occurs, which is after
 * each hashchange, popstate, or triggerAppChange, even if no changes to registered applications were necessary.
 * @see https://single-spa.js.org/docs/api/#before-routing-event
 */
window.addEventListener('single-spa:before-routing-event', async (event: CustomEvent) => {
  const { oldUrl, newUrl, cancelNavigation, originalEvent } = event.detail;

  LoggerUtilsComponent.getInstance().info(`[main.ts] before routing event {oldUrl=${oldUrl}, newUrl=${newUrl}}`);

  if (newUrl.includes('recovery') && !oldUrl.includes('recovery')) {
    sessionStorage.setItem('isFromAnotherPage', 'true');
  }

  if (newUrl.includes('landing') || newUrl.includes('enrollment') || newUrl.includes('multiholder-selector') || newUrl.includes('recovery')) {
    checkNavigatorService.checkNavigator(oldUrl);
  }

  if (oldUrl.includes('searcher-page') && newUrl.includes('postal-address')) {
    sessionStorage.removeItem('fiscal');
  }

  if (
    (oldUrl.includes('onboarding/results-messages/mantinance') && !newUrl.includes('onboarding/results-messages/mantinance')) ||
    (newUrl === oldUrl && !newUrl.includes('results-messages/mantinance'))
  ) {
    sessionStorage.removeItem('isInMantinance');
  }

  if (!sessionStorage.getItem('isInMantinance')) {
    const params = await paramsService.getParams('INIT-EXPANSIONDIGITAL').then((response) => {
      return (response as any).data.params;
    });

    for (const param of params) {
      if (param.code === 'ONB-ENABLED' && param.value === 'false' && !sessionStorage.getItem('isInMantinance')) {
        sessionStorage.setItem('isInMantinance', 'true');
        navigateToUrl('results-messages/mantinance');
      }
      if (param.code === 'MULTIOWNER-ENABLED') {
        param.value === 'true' ? ((portalConfig as any).HOLDER_IS_ENABLED = true) : ((portalConfig as any).HOLDER_IS_ENABLED = false);
        if (!sessionStorage.getItem('HOLDER_IS_ENABLED')) {
          setPortalConfig(portalConfig);
          sessionStorage.setItem('HOLDER_IS_ENABLED', (portalConfig as any).HOLDER_IS_ENABLED.toString());
        }
      }
    }
  }

  const deniedUrls: string[] = ['contract-signing'];

  const allowedUrls: string[] = ['enrollment', 'landing'];
  const isDeniedUrl = deniedUrls.some((url) => new URL(oldUrl).pathname.includes(url));
  const isAllowedUrl = allowedUrls.some((url) => new URL(newUrl).pathname.includes(url));
  const config = getSharedConfig('MANUAL_RECOVERY_LOGIN');
  const url =
    config.portalConfig.REST_CONFIG.HOST + '/' + config.commonMfeConfig.basePath + 'recovery-login?language=' + config.commonMfeConfig.language;
  let message: string;
  let title: string;
  let acceptButton: string;
  let rejectButton: string;

  switch (config.commonMfeConfig.language) {
    case 'es-ES':
      title = '¿Quieres salir del sitio?';
      message = 'Es posible que tengas que introducir de nuevo tus datos.';
      acceptButton = 'Aceptar';
      rejectButton = 'Cancelar';
      break;
    case 'ca-ES':
      title = 'Vols sortir del lloc?';
      message = 'És possible que hagis de tornar a introduir les teves dades.';
      acceptButton = 'Acceptar';
      rejectButton = 'Cancel·lar';
      break;
    case 'en-GB':
      title = 'Do you want to leave the site?';
      message = 'You may have to re-enter your data.';
      acceptButton = 'Accept';
      rejectButton = 'Cancel';
      break;
    default:
      title = '¿Quieres salir del sitio?';
      message = 'Es posible que tengas que introducir de nuevo tus datos.';
      acceptButton = 'Aceptar';
      rejectButton = 'Cancelar';
  }

  const backModal = document.getElementsByClassName('back-modal')[0] as any;
  backModal.setAttribute('style', '--_bs-modal-width: 95%; --_bs-modal-min-height: 370px;');
  const buttonAccept = document.getElementById('accept');
  const buttonReject = document.getElementById('reject');

  if (isDeniedUrl && !isAllowedUrl) {
    if (originalEvent !== undefined) {
      if (originalEvent.isTrusted === true) {
        document.getElementById('modal-title').innerText = title;
        document.getElementById('modal-text').innerText = message;
        buttonAccept.innerText = acceptButton;
        buttonReject.innerText = rejectButton;
        window.history.pushState(null, null, oldUrl);
        backModal.type = 'pop-up';
        backModal.open();

        buttonAccept.addEventListener('click', () => {
          backModal.close();
          sessionStorage.removeItem('documentsPrevInfo');
          sessionStorage.removeItem('recoveryKyc');
          sessionStorage.removeItem('documentsKyc');
          sessionStorage.removeItem('postalAddress');
          window.history.pushState(null, null, url);
        });

        buttonReject.addEventListener('click', () => {
          backModal.close();
        });
      }
    }
  }

  if (new URL(newUrl).pathname === '/unauthorized') {
    LoggerUtilsComponent.getInstance().debug('[main.ts] new URL(newUrl).pathname === /unauthorized');
    // TODO: handle unauthorized routes from some conditions
    cancelNavigation();
  }
});

/**
 * A single-spa:routing-event event is fired every time that a routing event has occurred, which is after each
 * hashchange, popstate, or triggerAppChange, even if no changes to registered applications were necessary;
 * and after single-spa verified that all apps were correctly loaded, bootstrapped, mounted, and unmounted.
 * @see https://single-spa.js.org/docs/api/#routing-event
 */
window.addEventListener('single-spa:routing-event', (event: CustomEvent) => {
  const { originalEvent, newAppStatuses, appsByNewStatus, totalAppChanges } = event.detail;
  LoggerUtilsComponent.getInstance().debug(
    `[main.ts] routing event {originalEvent=${JSON.stringify(originalEvent)}, newAppStatuses=${JSON.stringify(
      newAppStatuses
    )}, appsByNewStatus=${JSON.stringify(appsByNewStatus)}, totalAppChanges=${totalAppChanges}}`
  );
});
